import type { QueryClient } from "@tanstack/react-query";

/**
 * TROCAR DE CONTA APAGA O CACHE. Esta é a regra, e ela existe por um
 * vazamento REAL entre contas.
 *
 * O que acontecia: sair e entrar com outra conta não recarrega a página —
 * é tudo navegação dentro do mesmo aplicativo. O estado do React é
 * substituído, mas o cache do React Query sobrevive inteiro. E NENHUMA chave
 * de consulta carrega a conta: são `["mensagens", pasta]`, `["pastas"]`,
 * `["contatos"]`. Então a tela da conta nova pedia `["mensagens","INBOX"]`,
 * encontrava a resposta pronta da conta ANTERIOR, e mostrava o e-mail de
 * outra pessoa — com o nome e o avatar de quem acabou de entrar.
 *
 * Aconteceu de verdade: entrando com `jhou@jhou.tec.br` depois de sair de
 * `contato1@projetoanjinhosdarua.com.br`, a caixa de entrada mostrava as
 * mensagens do contato1. O jeito de corrigir era recarregar a página, porque
 * o cache só vive em memória.
 *
 * O SERVIDOR NUNCA ENTREGOU DADO ERRADO. Toda consulta de correio filtra por
 * `usuario_id` tirado do token da sessão — conferido. O que vazou foi a
 * resposta de ANTES, guardada aqui, para olhos que não eram os de antes.
 *
 * POR QUE A REGRA MORA NUMA FUNÇÃO SÓ: a alternativa seria pôr a conta em
 * cada chave de consulta — são mais de quarenta, espalhadas por toda a
 * interface, e a quadragésima primeira nasceria sem ela. Aqui existe UM
 * ponto por onde a identidade muda, e ele sempre limpa. É a mesma escolha da
 * trava de leitura da migração e da guarda de escopo da API: amarrar a regra
 * à pergunta, não repeti-la em cada lugar que a usa.
 *
 * Devolve `true` quando limpou, para o chamador poder afirmar isso em teste.
 */
export function trocarDeConta(
  fila: QueryClient,
  de: string | null | undefined,
  para: string | null | undefined,
): boolean {
  // Normaliza, porque "ninguém logado" aparece como null e como undefined
  // dependendo de onde vem — e `null !== undefined` limparia o cache a cada
  // carregamento de página sem motivo.
  const anterior = de ?? null;
  const proximo = para ?? null;
  if (anterior === proximo) return false;

  // Cancelar ANTES de limpar: uma requisição em voo da conta anterior ainda
  // pode estar para responder, e ela não pode repovoar o cache depois da
  // limpeza. Sem isto sobraria uma janela estreita — mas é justamente numa
  // janela estreita que este defeito vive.
  void fila.cancelQueries();
  fila.clear();
  return true;
}

/**
 * TROCAR DE IDENTIDADE: o cache E a credencial, no mesmo ponto.
 *
 * ESTA FUNÇÃO NASCEU DE UM DEFEITO, em 15/10/2026, e o defeito foi
 * exatamente o que o comentário acima previa — só que na outra metade.
 *
 * Entrar na caixa de um cliente (personificação) trocava o ESTADO: a faixa
 * vermelha no topo mostrava `contato@kasperplanosdesaude.com.br`, e o cache
 * era limpo corretamente. Mas o TOKEN não trocava. O cliente HTTP continuava
 * mandando o do administrador, e o servidor — corretamente — respondia com a
 * caixa do administrador.
 *
 * O resultado é a pior forma de errar que existe aqui: a tela AFIRMAVA estar
 * numa caixa e MOSTRAVA outra. Pior que não funcionar, porque não parece
 * quebrado. Quem acreditasse na faixa responderia um e-mail achando que fala
 * pelo cliente, ou concluiria que a migração do cliente veio vazia.
 *
 * A lição é a mesma de `trocarDeConta`, aplicada ao que faltava: identidade
 * tem DUAS metades — quem o servidor acha que você é (o token) e o que a tela
 * mostra (o cache e o estado). Elas trocam juntas ou não trocam. Separá-las
 * em dois lugares é garantir que um dia alguém mexa em um e esqueça o outro.
 *
 * O depósito entra como PARÂMETRO para esta função poder ser testada sem
 * navegador. Ver cache.test.ts.
 */
export interface DepositoDeToken {
  guardarToken(token: string | null): void;
  /**
   * Registra de QUEM é esta sessão. Entra junto com o token porque a
   * renovação precisa saber com quem comparar o que o servidor devolver —
   * ver o comentário em api/deposito.ts e o defeito das duas abas.
   */
  guardarConta(caixa: string | null, personificando?: boolean): void;
  limpar(): void;
}

export function trocarDeIdentidade(
  fila: QueryClient,
  deposito: DepositoDeToken,
  de: string | null | undefined,
  nova: {
    caixa?: string | null;
    token?: string | null;
    personificada_por?: string | null;
  } | null,
): boolean {
  // A CREDENCIAL PRIMEIRO, e a ordem importa: limpar o cache dispara as
  // consultas das telas que estão montadas. Se o token velho ainda estivesse
  // no lugar nesse instante, a primeira leva de requisições sairia com a
  // identidade ANTERIOR — e repovoaria o cache recém-limpo com dados da
  // pessoa errada, que é o defeito original de volta por outra porta.
  if (nova?.token) {
    deposito.guardarToken(nova.token);
  } else if (!nova) {
    deposito.limpar();
  }
  // A CONTA ANDA COM O TOKEN, pelo mesmo motivo que o token anda com o cache:
  // são metades da mesma identidade, e separá-las em dois lugares garante que
  // um dia alguém mexa numa e esqueça a outra. Fora do ramo acima de
  // propósito — mesmo quando a sessão chega sem token novo (retomada), a
  // conta precisa ficar registrada, senão a renovação seguinte não tem com
  // o que comparar e volta a adotar qualquer identidade em silêncio.
  if (nova) {
    deposito.guardarConta(nova.caixa ?? null, Boolean(nova.personificada_por));
  }
  return trocarDeConta(fila, de, nova?.caixa ?? null);
}
